import { useContext, useMemo } from 'react';

import { ColDef } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { format, isValid, parseISO } from 'date-fns';
import { Dialog } from 'primereact/dialog';
import { useSearchParams } from 'react-router-dom';

import { WithLocalStorage } from '@/components';
import { useDefaultGridOptions } from '@/grid';
import { defaultColDef } from '@/grid/defaults';
import { PrimaryContext } from '@/services';
import { useCreatedFertsQuery } from '@/services/fertilization/hooks';

import { FertilizationButtons } from './FertilizationButtons';
import { FertilizationCalculations } from './FertilizationCalculations';
import { FertilizationConfig } from './FertilizationConfig';
import { FertAccount } from './grid-cell-rendered-components/FertAccount';
import { FertName } from './grid-cell-rendered-components/FertName';
import { CreatedFertType } from './types';
import { useFertilizationContext } from './useFertilizationContext';

import './Fertilization.css';

type Props = {
  className?: string;
};
export const FertilizationToolbar = ({ className }: Props) => {
  const defaultGridOptions = useDefaultGridOptions();
  const { themeMode } = useContext(PrimaryContext);
  const { showTodaysFerts, setShowTodaysFerts } = useFertilizationContext();
  const [params] = useSearchParams();

  const fertilizationDateRaw = params.get('date');
  const fertilizationSite = params.get('site');
  let fertilizationDate = fertilizationDateRaw;
  if (fertilizationDateRaw) {
    const parsed = parseISO(fertilizationDateRaw);
    if (isValid(parsed)) {
      fertilizationDate = format(parsed, 'MM-dd-yyyy');
    }
  }

  const createdFertsRes = useCreatedFertsQuery({
    donorScheduleDate: fertilizationDate,
    fertSiteId: fertilizationSite,
  });

  const createdFertsData = createdFertsRes.data;

  const createdFertsColDefs: ColDef<CreatedFertType>[] = useMemo(
    () => [
      {
        field: 'fertName',
        cellRenderer: FertName,
        width: 300,
      },
      {
        field: 'semenType',
        width: 80,
      },
      {
        field: 'accountNumber',
        cellRenderer: FertAccount,
        headerName: 'Account',
        width: 110,
      },
      { field: 'numSemenUnits', headerName: 'Semen Units', width: 130 },
      { field: 'ivfNum', headerName: 'IVFNum', width: 100 },
      { field: 'matTime', headerName: 'MatTime', width: 100 },
      { field: 'exportName', headerName: 'Export', width: 100 },
      { field: 'fertComment', headerName: 'Comment', width: 150 },
      { field: 'fertComments', headerName: 'Comment List', width: 200 },
    ],
    [],
  );

  return (
    <div className={`fertilization__toolbar ${className}`.trim()}>
      <FertilizationConfig />
      <FertilizationCalculations />
      <FertilizationButtons />
      <Dialog
        className="fertilization__todays-ferts"
        header="Todays Created Ferts"
        visible={showTodaysFerts}
        onHide={() => setShowTodaysFerts(false)}
      >
        <WithLocalStorage storageKey="fertilization-toolbar-grid">
          <AgGridReact
            gridOptions={defaultGridOptions}
            className={`ag-theme-transova${themeMode === 'dark' ? '-dark' : ''} dark-borders`}
            defaultColDef={defaultColDef}
            columnDefs={createdFertsColDefs}
            rowData={createdFertsData}
            suppressRowVirtualisation={true}
          />
        </WithLocalStorage>
      </Dialog>
    </div>
  );
};
